import React, { useEffect } from 'react';
import Slider from 'react-slick';
import { Cable, Zap, Settings, Activity, ArrowRight } from 'lucide-react';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../styles/SliderStyles.css";

const Harness = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: true,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    dotsClass: "slick-dots custom-dots",
  };

  const productImages = [
    `${process.env.PUBLIC_URL}/images/harness/harness1.png`,
    `${process.env.PUBLIC_URL}/images/harness/harness2.png`,
    `${process.env.PUBLIC_URL}/images/harness/harness3.png`,
    `${process.env.PUBLIC_URL}/images/harness/harness4.png`,
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/banner.jpg)` }}
        >
          <div className="absolute inset-0 bg-slate-900/70"></div>
        </div>
        <div className="relative z-10 text-center px-4 animate-slide-up">
          <span className="text-secondary font-bold tracking-widest uppercase mb-4 block">Core Products</span>
          <h1 className="text-5xl md:text-6xl font-display font-bold text-white mb-6">Harness Cable</h1>
          <p className="text-xl text-slate-200 max-w-2xl mx-auto font-light">
            복잡한 배선 작업을 간소화하고 최적의 전기적 연결을 제공하는 고품질 하네스 케이블입니다.
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 bg-white">
        <div className="section-container">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-block p-3 bg-blue-50 rounded-lg text-primary mb-2">
                <Cable size={32} />
              </div>
              <h2 className="text-4xl font-display font-bold text-slate-900 leading-tight">
                산업 설비의 <span className="text-primary">신경망</span>을<br /> 구축합니다.
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                하네스 케이블은 전자 기기와 전기 장비의 다양한 부품을 유기적으로 연결하여
                신호와 전력을 안정적으로 공급하는 핵심 부품입니다.
              </p>
              <ul className="space-y-4 pt-4">
                {[
                  "배선 작업의 단순화 및 공간 절약",
                  "전기적 노이즈 감소 및 신호 안정성 확보",
                  "유지보수 용이성 및 내구성 강화"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center text-slate-700 font-medium">
                    <ArrowRight size={18} className="text-accent mr-3" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-100 to-slate-100 rounded-2xl transform rotate-3 opacity-50"></div>
              <img
                src={`${process.env.PUBLIC_URL}/images/harness/harness1.png`}
                alt="Harness Introduction"
                className="relative rounded-2xl shadow-xl w-full object-cover h-[400px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Product Lineup */}
      <section className="py-24 bg-slate-50">
        <div className="section-container">
          <div className="text-center mb-16">
            <span className="text-primary font-bold tracking-widest uppercase text-sm">Product Lineup</span>
            <h2 className="text-4xl font-display font-bold text-slate-900 mt-2">주요 제품군</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Product 1 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300 group">
              <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center text-primary mb-6 group-hover:bg-primary group-hover:text-white transition-colors">
                <Settings size={28} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">PLC Harness</h3>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full mr-2"></span>Film Laminating</li>
                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full mr-2"></span>Encoder</li>
                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full mr-2"></span>Motor Connection</li>
              </ul>
            </div>

            {/* Product 2 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300 group">
              <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center text-secondary mb-6 group-hover:bg-secondary group-hover:text-white transition-colors">
                <Zap size={28} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">AC Servo Harness</h3>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full mr-2"></span>J2 / J3 Series</li>
                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full mr-2"></span>Yaskawa Series</li>
                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full mr-2"></span>High Power Servo</li>
              </ul>
            </div>

            {/* Product 3 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300 group">
              <div className="w-14 h-14 bg-green-50 rounded-xl flex items-center justify-center text-emerald-600 mb-6 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                <Activity size={28} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">I/O Board Harness</h3>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full mr-2"></span>HDRA68P</li>
                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full mr-2"></span>PCR96P</li>
                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full mr-2"></span>Custom Interface</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process Flow */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-slate-50 skew-x-12 transform origin-top-right z-0"></div>
        <div className="section-container relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-display font-bold text-slate-900">제조 공정</h2>
            <p className="text-slate-500 mt-4">체계적인 공정 관리를 통해 완벽한 품질을 구현합니다.</p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-4 md:p-8 border border-slate-100">
            <img
              src={`${process.env.PUBLIC_URL}/images/harness_process.jpg`}
              alt="Harness Process Flow"
              className="w-full h-auto rounded-lg"
            />
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="section-container">
          <div className="text-center mb-16">
            <span className="text-accent font-bold tracking-widest uppercase text-sm">Gallery</span>
            <h2 className="text-4xl font-display font-bold mt-2">제품 갤러리</h2>
          </div>

          <Slider {...settings} slidesToShow={3} className="products-slider -mx-4" responsive={[
            {
              breakpoint: 1024,
              settings: {
                slidesToShow: 2,
              }
            },
            {
              breakpoint: 640,
              settings: {
                slidesToShow: 1,
              }
            }
          ]}>
            {productImages.map((src, idx) => (
              <div key={idx} className="outline-none px-4">
                <div className="aspect-[4/3] bg-white rounded-2xl overflow-hidden flex items-center justify-center p-4 hover:shadow-xl transition-shadow duration-300">
                  <img src={src} alt={`Harness Product ${idx + 1}`} className="max-w-full max-h-full object-contain hover:scale-105 transition-transform duration-500" />
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </section>
    </div>
  );
};

interface ArrowProps {
  onClick?: () => void;
  style?: React.CSSProperties;
  className?: string;
}

const CustomPrevArrow: React.FC<ArrowProps> = (props) => {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="absolute left-0 z-10 p-3 text-white bg-slate-800/50 hover:bg-primary hover:scale-110 rounded-full transition-all duration-300 transform -translate-y-1/2 top-1/2 backdrop-blur-sm shadow-lg border border-white/10"
      style={{ left: '-20px' }}
      aria-label="Previous"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
    </button>
  );
};

const CustomNextArrow: React.FC<ArrowProps> = (props) => {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="absolute right-0 z-10 p-3 text-white bg-slate-800/50 hover:bg-primary hover:scale-110 rounded-full transition-all duration-300 transform -translate-y-1/2 top-1/2 backdrop-blur-sm shadow-lg border border-white/10"
      style={{ right: '-20px' }}
      aria-label="Next"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
};

export default Harness;
